import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getApi } from 'views/services/api';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import Customization from '../Customization';
import PreReservationNotifier from './PreReservationNotifier';
import OnboardingGate from 'views/Onboarding/OnboardingGate';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    'margin',
    open
      ? {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        }
      : {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }
  ),
  [theme.breakpoints.up('md')]: {
    marginLeft: open ? 0 : -(drawerWidth - 20),
    width: `calc(100% - ${drawerWidth}px)`
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '20px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '10px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px',
    marginRight: '10px'
  }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };

  const location = useLocation();
  const navigate = useNavigate();

  // -------------------------------------------------------------------------
  // Paywall en runtime: re-verifica la vigencia de la suscripción contra el
  // backend (el endpoint de pagos refresca los status por fecha). Si venció
  // — p. ej. terminaron los días gratis del trial — actualiza localStorage y
  // lleva al hotel directo a la página de pago sin esperar a que re-loguee.
  // -------------------------------------------------------------------------
  const checkSubscriptionVigencia = async () => {
    let hotelData;
    try {
      hotelData = JSON.parse(localStorage.getItem('hotelData') || 'null');
    } catch (e) {
      return;
    }
    if (!hotelData || hotelData.role === 'admin') return;
    const hotelId = hotelData.hotelId || hotelData._id;
    if (!hotelId) return;
    try {
      const res = await getApi(`api/payments/list/?createdBy=${hotelId}`);
      if (!res || res.status !== 200) return;
      const vigente = (res.data?.paymentsData || []).some((p) => p.status === 'active' || p.status === 'expiresSoon');
      const fresh = JSON.parse(localStorage.getItem('hotelData') || '{}');
      const newStatus = vigente ? 'active' : 'expired';
      if (fresh.subscriptionStatus !== newStatus) {
        fresh.subscriptionStatus = newStatus;
        localStorage.setItem('hotelData', JSON.stringify(fresh));
        if (!vigente) navigate('/dashboard/hotelsubscriptions', { replace: true });
      }
    } catch (e) {
      /* noop — sin red no bloqueamos al usuario */
    }
  };

  // Al navegar: máximo un chequeo cada 2 minutos para no saturar el backend.
  useEffect(() => {
    const last = Number(sessionStorage.getItem('lastSubscriptionCheck') || 0);
    if (Date.now() - last < 2 * 60 * 1000) return;
    sessionStorage.setItem('lastSubscriptionCheck', String(Date.now()));
    checkSubscriptionVigencia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Y aunque no navegue: chequeo periódico cada 2 minutos.
  useEffect(() => {
    const intervalId = setInterval(() => {
      sessionStorage.setItem('lastSubscriptionCheck', String(Date.now()));
      checkSubscriptionVigencia();
    }, 2 * 60 * 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hotelDataStr = localStorage.getItem('hotelData');
  let subscriptionExpired = false;

  if (hotelDataStr) {
    try {
      const hotelData = JSON.parse(hotelDataStr);
      if (hotelData.role !== 'admin' && hotelData.subscriptionStatus === 'expired') {
        subscriptionExpired = true;
      }
    } catch (e) {
      console.error('Error parsing hotelData in MainLayout', e);
    }
  }

  // Permitimos quedarse en la página de pagos/suscripciones y en la
  // landing de éxito de Stripe (donde se confirma el cobro y se libera
  // el paywall). Sin esta excepción, el usuario que vuelve de Stripe
  // sería rebotado a /dashboard/hotelsubscriptions antes de que el
  // frontend pueda actualizar localStorage.subscriptionStatus.
  const paywallAllowedPath =
    location.pathname.includes('/dashboard/hotelsubscriptions') || location.pathname.includes('/dashboard/hotelpayments/stripe-success');

  if (subscriptionExpired && !paywallAllowedPath) {
    return <Navigate to="/dashboard/hotelsubscriptions" replace />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
        }}
      >
        <Toolbar>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

      {/* main content */}
      <Main theme={theme} open={leftDrawerOpened}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet />
      </Main>
      <Customization />
      {/* 🔔 Notificación en tiempo real para nuevas prereservas */}
      <PreReservationNotifier />
      {/* 🧭 Wizard de configuración inicial para hoteles nuevos */}
      <OnboardingGate />
    </Box>
  );
};

export default MainLayout;
